import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipModeContext = React.createContext<"tooltip" | "popover">("tooltip");

const Tooltip = ({
  children,
  delayDuration,
  disableHoverableContent,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const isMobile = useIsMobile();
  const mode: "tooltip" | "popover" = isMobile ? "popover" : "tooltip";

  if (mode === "popover") {
    return (
      <TooltipModeContext.Provider value="popover">
        <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>
      </TooltipModeContext.Provider>
    );
  }

  return (
    <TooltipModeContext.Provider value="tooltip">
      <TooltipPrimitive.Root
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    </TooltipModeContext.Provider>
  );
};

const useTooltipMode = () => React.useContext(TooltipModeContext);

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ ...props }, ref) => {
  const mode = useTooltipMode();
  if (mode === "popover") {
    return <PopoverPrimitive.Trigger ref={ref} {...props} />;
  }
  return <TooltipPrimitive.Trigger ref={ref} {...props} />;
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipContentInner
    ref={ref}
    sideOffset={sideOffset}
    className={className}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipContentInner = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  const mode = useTooltipMode();
  const contentClassName = cn(
    "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
    className,
  );

  if (mode === "popover") {
    return (
      <PopoverPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={contentClassName}
        {...props}
      />
    );
  }

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={contentClassName}
      {...props}
    />
  );
});
TooltipContentInner.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
